// ============================================================================
// hotelStore.js — Single source of truth for "the user's saved hotel"
//
// Offline-first. The hotel always lives in `localStorage["habano.hotel"]`
// with this shape:
//
//   {
//     name: "My Hotel",
//     address: "" | string,
//     lat: number,
//     lng: number,
//     saved_at: ISO 8601 string,
//     source: "gps" | "manual" | "picker"
//   }
//
// All consumers in the app (Journey, Car, CarMap, Map, FriendProfile, etc.)
// must read via `readHotel()` and write via `writeHotel()`. No other storage
// key is used anywhere for the hotel.
//
// When the user is signed in AND the device is online, `syncHotelIfOnline()`
// fires a background POST to `/me/saved-locations`. The localStorage write is
// always the source of truth; the API call is best-effort.
// ============================================================================
import { lsGet, lsSet, lsRemove } from "./safeStorage";
import { api } from "./api";

const KEY = "habano.hotel";
// Legacy key (iter133 and earlier) — read-once for migration.
const LEGACY_KEY = "habano.guest.hotel";

/** Read the saved hotel from localStorage. Returns null when not set. */
export function readHotel() {
  let raw = lsGet(KEY);
  if (!raw) {
    const legacy = lsGet(LEGACY_KEY);
    if (legacy) {
      // One-time migration — preserve old data under the new key.
      try {
        const obj = JSON.parse(legacy);
        if (obj && obj.name) {
          const migrated = {
            name: obj.name,
            address: obj.address || "",
            lat: typeof obj.lat === "number" ? obj.lat : 0,
            lng: typeof obj.lng === "number" ? obj.lng : 0,
            saved_at: obj.created_at || new Date().toISOString(),
            source: "legacy",
          };
          lsSet(KEY, JSON.stringify(migrated));
          lsRemove(LEGACY_KEY);
          raw = JSON.stringify(migrated);
        }
      } catch { /* ignore */ }
    }
  }
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    if (!obj || !obj.name) return null;
    return {
      // Stable id so React lists key correctly and Map can render a pin.
      id: "local-hotel",
      name: obj.name,
      category: "hotel",
      address: obj.address || "",
      lat: typeof obj.lat === "number" ? obj.lat : 0,
      lng: typeof obj.lng === "number" ? obj.lng : 0,
      saved_at: obj.saved_at || new Date().toISOString(),
      created_at: obj.saved_at || new Date().toISOString(),
      source: obj.source || "manual",
      __local: true,
    };
  } catch {
    return null;
  }
}

/** Persist the hotel to localStorage. Returns the canonical object. */
export function writeHotel(hotel) {
  if (!hotel || !hotel.name) return null;
  const payload = {
    name: hotel.name,
    address: hotel.address || "",
    lat: typeof hotel.lat === "number" ? hotel.lat : 0,
    lng: typeof hotel.lng === "number" ? hotel.lng : 0,
    saved_at: new Date().toISOString(),
    source: hotel.source || "manual",
  };
  lsSet(KEY, JSON.stringify(payload));
  // Flag a pending sync if the user is offline at save time.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    lsSet("habano.hotel.pending_sync", "1");
  }
  return readHotel();
}

/** Remove the saved hotel. */
export function clearHotel() {
  lsRemove(KEY);
  lsRemove("habano.hotel.pending_sync");
}

/**
 * If the device is online AND the caller is authed, push the hotel to the
 * backend. This is fire-and-forget — failures are swallowed and a pending
 * sync flag is left so we can retry on the next call.
 *
 * @param {boolean} authed True when the caller is signed in.
 * @returns {Promise<object|null>} The server-side row when sync succeeded.
 */
export async function syncHotelIfOnline(authed) {
  if (!authed) return null;
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    lsSet("habano.hotel.pending_sync", "1");
    return null;
  }
  const hotel = readHotel();
  if (!hotel) return null;
  try {
    // Look for an existing hotel saved-location, update or create.
    let existing = null;
    try {
      const r = await api.get("/me/saved-locations", { params: { category: "hotel" } });
      existing = Array.isArray(r.data) && r.data.length > 0 ? r.data[0] : null;
    } catch { /* ignore */ }

    const body = {
      name: hotel.name,
      category: "hotel",
      lat: hotel.lat || 0,
      lng: hotel.lng || 0,
      address: hotel.address || null,
      notes: `Saved via SaveHotelModal (${hotel.source})`,
    };
    let row = null;
    if (existing && existing.id) {
      const { data } = await api.put(`/me/saved-locations/${existing.id}`, body);
      row = data;
    } else {
      const { data } = await api.post("/me/saved-locations", body);
      row = data;
    }
    lsRemove("habano.hotel.pending_sync");
    return row;
  } catch {
    lsSet("habano.hotel.pending_sync", "1");
    return null;
  }
}

/**
 * Convenience for auto-sync on app start: if a pending-sync flag was set
 * during an offline save, call this to flush it. Safe to call repeatedly.
 */
export async function flushPendingHotelSync(authed) {
  if (!lsGet("habano.hotel.pending_sync")) return null;
  return syncHotelIfOnline(authed);
}
